/**
 * Single source of truth for "the EIP-1193 provider this app should use".
 *
 * Resolution priority:
 *   1. Already-cached provider (avoids re-prompting on subsequent connect calls)
 *   2. Active WalletConnect session (if user previously connected via WC)
 *   3. Synchronously-available injected provider (window.ethereum, EIP-6963 already announced, etc.)
 *   4. Wait briefly for late EIP-6963 announce
 *   5. Fall back to WalletConnect modal (only when caller asks for it)
 *
 * The returned provider is an EIP-1193 object — callers use `provider.request(...)`
 * and `provider.on(...)` exactly the same way regardless of source.
 */

import {
  getBrowserEthereumProvider,
  waitForBrowserEthereumProvider
} from '@/utils/evmInjectedProvider'
import {
  connectViaWalletConnect,
  getActiveWalletConnectProvider,
  disconnectWalletConnect
} from '@/utils/walletConnectProvider'
import { uistep, uiwarn } from '@/utils/uiLogger'

let cachedProvider = null
let cachedSource = '' // 'injected' | 'wc'

function setCache(p, source) {
  cachedProvider = p
  cachedSource = source
  return p
}

/**
 * Synchronously look for an already-resolved provider. Does NOT trigger any
 * UI (no WC modal). Used by code paths that need to know "are we already
 * connected" without prompting the user.
 */
export function getResolvedProviderSync() {
  if (cachedProvider) return cachedProvider
  const wc = getActiveWalletConnectProvider()
  if (wc) return setCache(wc, 'wc')
  const inj = getBrowserEthereumProvider()
  if (inj) return setCache(inj, 'injected')
  return null
}

export function getResolvedProviderSource() {
  return cachedSource
}

/**
 * Resolve a provider for the current session.
 *
 * @param {object} [opts]
 * @param {number} [opts.timeoutMs=3000] — how long to wait for late EIP-6963 announce
 * @param {boolean} [opts.allowWalletConnectFallback=false] — if no injected provider after timeout, open the WC modal
 * @param {boolean} [opts.preferWalletConnect=false] — skip injected detection entirely and open WC directly
 * @returns {Promise<object|null>} EIP-1193 provider or null if nothing resolved
 */
export async function resolveProvider(opts = {}) {
  const {
    timeoutMs = 3000,
    allowWalletConnectFallback = false,
    preferWalletConnect = false
  } = opts

  if (cachedProvider) return cachedProvider

  if (preferWalletConnect) {
    return await connectAndCacheWalletConnect()
  }

  const wc = getActiveWalletConnectProvider()
  if (wc) return setCache(wc, 'wc')

  const sync = getBrowserEthereumProvider()
  if (sync) {
    uistep('RESOLVE', 'using injected provider (sync)')
    return setCache(sync, 'injected')
  }

  const waited = await waitForBrowserEthereumProvider({ timeoutMs })
  if (waited) {
    uistep('RESOLVE', 'using injected provider (after wait)')
    return setCache(waited, 'injected')
  }

  if (allowWalletConnectFallback) {
    uistep('RESOLVE', 'no injected provider — falling back to WalletConnect')
    return await connectAndCacheWalletConnect()
  }

  uiwarn('[RESOLVE] no provider available and WC fallback not requested')
  return null
}

/** Force WalletConnect (skip injected). Returns null if user cancels. */
export async function forceConnectWalletConnect() {
  return await connectAndCacheWalletConnect()
}

async function connectAndCacheWalletConnect() {
  try {
    const wc = await connectViaWalletConnect()
    if (wc) return setCache(wc, 'wc')
    return null
  } catch (e) {
    uiwarn('[RESOLVE] WalletConnect connect failed/cancelled:', e && e.message ? e.message : e)
    return null
  }
}

/**
 * Reset the cached provider so the next `resolveProvider` call re-runs the
 * full resolution chain. Optionally tears down the active WC session too.
 */
export async function clearResolvedProvider({ disconnectWc = false } = {}) {
  if (disconnectWc) {
    await disconnectWalletConnect()
  }
  cachedProvider = null
  cachedSource = ''
}
